const fakeLocation = { pathname: window.location?.pathname || "/" };

import React, { useState, useEffect } from "react";
import { Message, UIState } from "./types/chat";
import { scrollToBottom } from "./utils/scrollToBottom";
import ChatHeader from "./components/ChatHeader";
import ChatMessages from "./components/ChatMessages";
import ChatInput from "./components/ChatInput";
import { useChatAPI } from "./hooks/useChatAPI";
import { useDynamicUI } from "./hooks/useDynamicUI";
import "./styles/typography.css";
import styles from "./styles/ChatClassification.module.css";

const ChatWithClassification: React.FC = () => {
  const location = fakeLocation;
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uiState, setUiState] = useState<UIState>({
    title: "Bem-vindo ao assistente virtual do FORINSIA!",
    subTitle: "Coloque aqui as suas questões ou dúvidas sobre a utilização do software.",
    examples: [],
    supportLink: "https://suporte.insia.pt/"
  });

  const { sendQuestion } = useChatAPI();
  useDynamicUI({ dynamicRoute: null, location, setUiState });

  // Mensagem inicial (boas-vindas)
  useEffect(() => {
    if (messages.length === 0) {
      const welcome: Message = {
        id: "welcome",
        text: `${uiState.title}\n\n${uiState.subTitle}\n\nConsulte o portal de suporte da INSIA [aqui](${uiState.supportLink}).`,
        sender: "bot",
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages([welcome]);
    }
  }, [uiState]);

  useEffect(() => {
    (document.querySelector("textarea") as HTMLTextAreaElement)?.focus();
    const scrollDelay = setTimeout(() => {
      scrollToBottom("." + styles.chatMessages);
    }, 100); // pequeno atraso para deixar o Framer Motion terminar
    return () => clearTimeout(scrollDelay);
  }, [messages]);


  const sendMessage = async () => {
    if (!inputText.trim()) return;

    const userMsg: Message = {
      id: Date.now(),
      text: inputText,
      sender: "user",
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputText("");
    setIsLoading(true);

    try {
      const botResponse = await sendQuestion({ question: inputText, messages, location });
      setMessages(prev => [...prev, botResponse]);
    } catch {
      setMessages(prev => [
        ...prev,
        {
          id: "error-" + Date.now(),
          text: "Erro de comunicação. Verifique a ligação.",
          sender: "bot",
          isError: true
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Minimizar (para integração ASP.NET)
  const handleMinimize = () => {
    const container = document.getElementById("chatbot-widget-container");
    if (container) {
      container.style.opacity = "0";
      container.style.transform = "translateY(20px)";
      setTimeout(() => (container.style.display = "none"), 250);
    }
  };

  return (
    <div className={styles.chatContainer}>
      <ChatHeader onMinimize={handleMinimize} />
      <ChatMessages messages={messages} isLoading={isLoading} />
      <ChatInput
        inputText={inputText}
        setInputText={setInputText}
        sendMessage={sendMessage}
        handleKeyPress={handleKeyPress}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ChatWithClassification;
